import {makeAutoObservable, observable, toJS} from 'mobx'
import type {z} from 'zod'
import {ctx} from 'new/ctx'
import {type IStorageService} from 'types/IStorageService'
import {type LocalStorageKey, localStorageSchemas} from 'types/LocalStorageKey'

////////////////////////////////////////////////////////////////////////////////

export class LocalStorageService
  implements IStorageService<LocalStorageKey, typeof localStorageSchemas>
{
  constructor() {
    ctx.localStorageService = this
    makeAutoObservable(this)
    this.#syncFromDocument()
    this.#initEffects()
  }

  /**
   * This map exists so we can easily access and manipulate localStorage values in a reactive way.
   *
   * As an example, because we are using this intermediary map the following code becomes
   * reactive.
   * get myLocalStorageItem() {
   *  return this.#localStorageService.get(LocalStorageKey.MY_LOCAL_STORAGE_ITEM)
   * }
   */
  private values = observable.map<string, unknown>()

  #abortController = new AbortController()

  /**
   * @deprecated use get()
   */
  dangerouslyGet<T = any>(key: string): T | undefined {
    const rawValue = localStorage.getItem(key)

    if (rawValue === null) {
      return undefined
    }

    try {
      return JSON.parse(rawValue) as T
    } catch (error) {
      return undefined
    }
  }

  /**
   * Returns the value of the storage with the given key, or undefined if it doesn't exist.
   */
  get<TKey extends LocalStorageKey>(key: TKey) {
    const rawValue = toJS(this.values.get(key))
    if (rawValue === undefined) {
      return undefined
    }
    const schema = localStorageSchemas[key]
    const {data} = schema.safeParse(rawValue)
    return data as z.infer<(typeof localStorageSchemas)[TKey]>
  }

  /**
   * Returns the value of the storage with the given key, or the default value if it doesn't exist.
   */
  getOrUseDefault<TKey extends LocalStorageKey>(key: TKey) {
    const rawValue = toJS(this.values.get(key))
    const schema = localStorageSchemas[key]
    const {data} = schema.safeParse(rawValue)
    return data as z.infer<(typeof localStorageSchemas)[TKey]>
  }

  set<T extends LocalStorageKey>(
    key: T,
    value: z.infer<(typeof localStorageSchemas)[T]>
  ) {
    localStorage.setItem(key, JSON.stringify(value))

    this.values.set(key, value)
  }

  has(key: LocalStorageKey) {
    return !!localStorage.getItem(key)
  }

  remove(key: LocalStorageKey) {
    localStorage.removeItem(key)
    this.values.delete(key)
  }

  #initEffects() {
    // Because of this event listener we can change the localStorage in the
    // devtools and the UI should update instantly without reloading the page.
    addEventListener(
      'storage',
      e => {
        if (!e.key) {
          return
        }

        this.#syncFromDocument()
      },
      {signal: this.#abortController.signal}
    )
  }

  #syncFromDocument() {
    // Getting all local storage keys: https://stackoverflow.com/a/51647623
    const allKeys = Object.keys({...localStorage})

    allKeys.forEach(key => {
      // eslint-disable-next-line deprecation/deprecation
      this.values.set(key, this.dangerouslyGet(key))
    })
  }

  dispose() {
    this.#abortController.abort()
  }
}
